/**
 * Offline / seed-mode client registry overlay.
 *
 * ``SEED_CLIENTS`` is the built-in stub list. Status + extra clients live in
 * localStorage so Manage Crew → Clients can deactivate and add without a BFF.
 */

import { SEED_CLIENTS } from "@/lib/active-client";
import type { ClientEntry } from "@/lib/docs-api";

const STATUS_KEY = "xaes-seed-client-status";
const EXTRA_KEY = "xaes-seed-clients-extra";

type StatusMap = Record<string, "active" | "inactive">;

function readStatus(): StatusMap {
  try {
    const raw = localStorage.getItem(STATUS_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const out: StatusMap = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (v === "active" || v === "inactive") out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function writeStatus(map: StatusMap): void {
  try {
    localStorage.setItem(STATUS_KEY, JSON.stringify(map));
  } catch {
    /* storage unavailable */
  }
}

function readExtras(): ClientEntry[] {
  try {
    const raw = localStorage.getItem(EXTRA_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is ClientEntry =>
        !!e &&
        typeof e === "object" &&
        typeof (e as ClientEntry).id === "string" &&
        typeof (e as ClientEntry).name === "string",
    );
  } catch {
    return [];
  }
}

function writeExtras(entries: ClientEntry[]): void {
  try {
    localStorage.setItem(EXTRA_KEY, JSON.stringify(entries));
  } catch {
    /* storage unavailable */
  }
}

/** Full seed registry (built-ins + extras) with overlay status applied. */
export function listSeedClientEntries(): ClientEntry[] {
  const status = readStatus();
  const builtIn: ClientEntry[] = SEED_CLIENTS.map((c) => ({
    id: c.id,
    name: c.name,
    status: status[c.id] ?? c.status,
  }));
  const extras = readExtras().map((c) => ({
    ...c,
    status: status[c.id] ?? c.status ?? "active",
  }));
  const seen = new Set(builtIn.map((c) => c.id));
  return [...builtIn, ...extras.filter((c) => !seen.has(c.id))];
}

export function listActiveSeedClients(): ClientEntry[] {
  return listSeedClientEntries().filter((c) => c.status === "active");
}

export function setSeedClientStatus(
  clientId: string,
  status: "active" | "inactive",
): ClientEntry {
  if (clientId === "dxc" || clientId === "local") {
    // "local" is the seed stand-in for the home instance — keep it active.
    if (clientId === "local" && status === "inactive") {
      throw new Error("Cannot deactivate the local seed instance");
    }
  }
  const map = readStatus();
  map[clientId] = status;
  writeStatus(map);
  const entry = listSeedClientEntries().find((c) => c.id === clientId);
  if (!entry) throw new Error(`Unknown client: ${clientId}`);
  return entry;
}

export function addSeedClient(input: { id: string; name: string }): ClientEntry {
  const id = input.id.trim();
  const name = input.name.trim();
  if (!/^[a-z0-9][a-z0-9-]{1,31}$/.test(id)) {
    throw new Error("id must match lowercase slug rules");
  }
  const existing = listSeedClientEntries();
  if (existing.some((c) => c.id === id)) {
    throw new Error(`client id '${id}' already registered`);
  }
  const entry: ClientEntry = { id, name, status: "active" };
  writeExtras([...readExtras(), entry]);
  return entry;
}
